import { RudderAnalytics } from '@rudderstack/analytics-js'
import debounce from 'lodash/debounce'
import { useAuthStore } from '~/stores/auth'
import { Auth } from '~/constants/general'
import { COOKIE_SCRIPT_CATEGORIES } from '~/types/cookies'

const useRudderStack = (rudderAnalytics: RudderAnalytics) => {
  const route = useRoute()
  const authStore = useAuthStore()

  const user = computed(() => authStore.user)

  const acceptPerformanceCookies = getCookieConsentByCategory(COOKIE_SCRIPT_CATEGORIES.PERFORMANCE)

  const userData = computed(() => {
    if (!user.value) return {}

    const { userId, name, email } = user.value[Auth.auth0RolesProperty]
    const [firstName, lastName] = name.split(' ')

    return {
      email_encrypted: btoa(email),
      auth0_id: userId,
      email,
      name,
      firstName,
      lastName,
    }
  })

  const baseParams = computed(() => ({
    page_title: window.document.title,
    page_url: route.path,
    uri: window.location.pathname + window.location.search + window.location.hash,
    submission_time: new Date(),
    platform: 'terminal',
  }))

  /**
   * Identify the user in RudderStack
   */
  const identifyUser = () => {
    if (!acceptPerformanceCookies.value) return
    rudderAnalytics.identify(userData.value.email_encrypted, userData.value)
    rudderAnalytics.track('identified', { ...userData.value, ...baseParams.value })
  }

  /**
   * Track user log in
   */
  const trackLogIn = () => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value
    rudderAnalytics.track('logged_in', data)

    identifyUser()
  }

  /**
   * Track user log out
   */
  const trackLogOut = () => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value
    rudderAnalytics.track('logged_out', {
      ...data,
      ...baseParams.value,
    })
  }

  /**
   * Track page views. We debounce the event to avoid sending too many events
   * to RudderStack. Specifically, in the pages with tabs, where the event
   * was triggering twice.
   */
  const trackPageView = debounce(() => {
    if (!acceptPerformanceCookies.value) return
    const data = {
      ...baseParams.value,
      ...userData.value,
    }

    rudderAnalytics.track('page_viewed', data)
  }, 1000)

  /**
   * Track form submissions
   */
  const trackFormSubmissions = (formData: any) => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value

    rudderAnalytics.track('form_submitted', {
      ...data,
      ...formData,
      ...baseParams.value,
    })
  }

  /**
   * Track form start event in a form
   */
  const trackFormStart = (formData: any) => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value

    rudderAnalytics.track('form_start', {
      ...data,
      ...formData,
      ...baseParams.value,
    })
  }

  /**
   * Track form field changes in a form
   */
  const trackFormFieldChange = debounce(
    (formFieldData: {
      form_id: string | number
      form_name: string
      form_type: string
      field_name: string
      field_type: string
      field_value?: string | any
    }) => {
      if (!acceptPerformanceCookies.value) return
      const data = userData.value
      rudderAnalytics.track('field_changed', {
        ...formFieldData,
        ...data,
        ...baseParams.value,
      })
    },
    500,
  )

  /**
   * Track error shown in the website
   */
  const trackErrorShown = (errorData: {
    error_code: string | number
    error_message: string
    error_type: string
  }) => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value
    rudderAnalytics.track('error_shown', {
      ...data,
      ...errorData,
      ...baseParams.value,
    })
  }

  /**
   * Track chart interactions in the website. We debounced this function because
   * it's trigger on hover state over the chart and that event is triggered many times
   * in a short period of time.
   */
  const trackChartInteraction = debounce(
    (chartData: {
      chart_title: string
      chart_type: string
      start_date?: string
      end_date?: string
      current_filters?: object
    }) => {
      if (!acceptPerformanceCookies.value) return
      rudderAnalytics.track('chart_interaction', {
        ...userData.value,
        ...baseParams.value,
        ...chartData,
      } as any)
    },
    2000,
  )

  const trackFilterApplied = debounce(
    (eventData: { filter_values: object; filter_type: any; chart_title: string }) => {
      if (!acceptPerformanceCookies.value) return
      const data = userData.value

      rudderAnalytics.track('filter_applied', {
        ...data,
        ...baseParams.value,
        ...eventData,
      } as any)
    },
    1000,
  )

  /**
   * Track chart interactions in the website
   */
  const trackChartDownload = (
    chartData: {
      chart_title?: string
      chart_type?: string
      element_id?: string
      download_format?: 'png' | 'xlsx'
    } = { chart_type: 'line_chart' },
  ) => {
    if (!acceptPerformanceCookies.value) return
    const data = userData.value
    rudderAnalytics.track('chart_download', {
      ...data,
      ...chartData,
      ...baseParams.value,
    } as any)
  }

  /**
   * Track search done in the website
   */
  const trackSearchDone = debounce(
    (searchData: { search_query: string; search_results_count: number; search_type: string }) => {
      if (!acceptPerformanceCookies.value) return
      const data = userData.value

      rudderAnalytics.track('search_done', {
        ...data,
        ...searchData,
        ...baseParams.value,
      })
    },
    2000,
  )

  return {
    identifyUser,
    trackLogIn,
    trackLogOut,
    trackPageView,
    trackFormSubmissions,
    trackFormStart,
    trackFormFieldChange,
    trackErrorShown,
    trackChartInteraction,
    trackFilterApplied,
    trackChartDownload,
    trackSearchDone,
  }
}

export default useRudderStack
